/**
 * عقودُ الهويّة — <b>ما يحتاجه كلُّ تطبيقٍ خاصّ قبل أن يعرف مجالَه</b>.
 *
 * الدخولُ، ومن أنا، وما أملك، وأيُّ فرعٍ ومستودع. وهذه أربعتُها مشتركةٌ بين
 * الإدارة ونقطة البيع بالضرورة: كلاهما يدخل بالحساب نفسِه ويقرأ الصلاحيّاتِ
 * نفسَها من الخادم.
 *
 * <b>وعقودُ المجال ليست هنا</b>: المنتجُ والإيصالُ والتحويلُ في
 * <c>apps/admin</c>، والبيعُ والوردية في <c>apps/pos</c> حين تُكتب. وحزمةٌ
 * مشتركةٌ تجمع عقودَ كلّ مجالٍ تُجبر نقطةَ البيع على معرفة ما لا يعنيها.
 *
 * ⚠ و<c>Perm</c> <b>أسماءٌ لا قرار</b>: الرمزُ نصٌّ يطابق <c>Perms.cs</c>
 *   حرفيًّا، ومن يملكه يقرّره الخادمُ ويُرسله في <c>/me</c>. ولا تُشتقّ صلاحيّةٌ
 *   من أخرى هنا ولا في أيّ واجهة.
 */

/** أسماءُ الصلاحيات كما في `Perms` — ما تحتاجه الشاشاتُ منها. */
export const Perm = {
  draftView: 'intake.draft.view',
  draftCreate: 'intake.draft.create',
  draftEdit: 'intake.draft.edit',
  draftConfirm: 'intake.draft.confirm',
  productView: 'catalog.product.view',
  productCreate: 'catalog.product.create',
  productEdit: 'catalog.product.edit',
  priceEdit: 'catalog.product.price.edit',
  movementView: 'inventory.movement.view',

  supplierView: 'purch.supplier.view',
  supplierEdit: 'purch.supplier.edit',
  purchaseView: 'purch.purchase.view',
  purchaseCreate: 'purch.purchase.create',
  purchaseEdit: 'purch.purchase.edit',
  purchaseConfirm: 'purch.purchase.confirm',
  purchaseCancel: 'purch.purchase.cancel',
  receiptView: 'purch.receipt.view',
  receiptCreate: 'purch.receipt.create',
  receiptPost: 'purch.receipt.post',
  receiptReverse: 'purch.receipt.reverse',
  receiptAckDuplicate: 'purch.receipt.ack_duplicate',
  costView: 'catalog.product.cost.view',

  /** نشرُ صنفٍ على الموقع العامّ — مستقلّةٌ عن `productEdit` (`docs/20` ق-٩). */
  productPublish: 'catalog.product.publish',
  movementReverse: 'inventory.movement.reverse',

  returnView: 'purch.return.view',
  returnCreate: 'purch.return.create',
  returnPost: 'purch.return.post',

  adjustmentView: 'inventory.adjustment.view',
  adjustmentPost: 'inventory.adjustment.post',

  transferView: 'inventory.transfer.view',
  transferCreate: 'inventory.transfer.create',
  transferShip: 'inventory.transfer.ship',
  transferReceive: 'inventory.transfer.receive',
  openingManage: 'inventory.opening.manage',

  /** ⚠ **ومستقلّةٌ عن `openingManage`**: الإغلاقُ يقع مرّةً في عمر المستودع،
      وإعادتُه تفتح بابَ الرصيدِ بلا مستندٍ من جديد. */
  openingReopen: 'inventory.opening.reopen',

  /** ⚠ **ومستقلّةٌ عن كتابة الأسعار**: من يُصفّي بضاعةً راكدةً ليس بالضرورة من يُسعّر. */
  saleView: 'sales.sale.view',
  customerView: 'sales.customer.view',
  customerEdit: 'sales.customer.edit',
  preorderView: 'sales.preorder.view',
  preorderHandle: 'sales.preorder.handle',
  webOrderView: 'sales.weborder.view',
  webOrderHandle: 'sales.weborder.handle',
  socialPublish: 'social.post.publish',
  socialManage: 'social.account.manage',

  // ── الذمم (المرحلة الخامسة) ────────────────────────────────────────────
  creditSell: 'sales.credit.sell',
  creditLimitOverride: 'sales.credit.override',
  creditLimitEdit: 'sales.credit.limit_edit',
  collectionView: 'ar.collection.view',
  collectionCreate: 'ar.collection.create',
  collectionReverse: 'ar.collection.reverse',
  receivableAdjust: 'ar.entry.adjust',

  // ── المحاسبة (المرحلة السادسة) ─────────────────────────────────────────
  ledgerView: 'acc.ledger.view',
  journalPost: 'acc.journal.post',
  journalReverse: 'acc.journal.reverse',
  accountEdit: 'acc.account.edit',

  // ── العمولات (المرحلة السابعة) ─────────────────────────────────────────
  commissionView: 'hr.commission.view',
  attendancePunch: 'hr.attendance.punch',
  attendanceEdit: 'hr.attendance.edit',
  cashSessionView: 'sales.cash_session.view',
  commissionRateEdit: 'hr.commission.rate_edit',
  commissionPay: 'hr.commission.pay',
  costFloorOverride: 'sales.cost_floor.override',
  settingEdit: 'platform.setting.edit',
  warehouseView: 'org.warehouse.view',
  warehouseEdit: 'org.warehouse.edit',
  branchView: 'org.branch.view',

  userView: 'identity.user.view',
  userCreate: 'identity.user.create',
  userEdit: 'identity.user.edit',
  userDisable: 'identity.user.disable',
  userResetPassword: 'identity.user.reset_password',
  userAssignRole: 'identity.user.assign_role',
  userAssignBranch: 'identity.user.assign_branch',
  userAssignWarehouse: 'identity.user.assign_warehouse',
  userRevokeSessions: 'identity.user.revoke_sessions',
  roleView: 'identity.role.view',
  roleEdit: 'identity.role.edit',

  // ── التقارير (المرحلة الرابعة) ─────────────────────────────────────────
  //
  // ⚠ **ومستقلّةٌ عن رموز اللوحة**: اللوحةُ نظرةٌ على اليوم، والتقريرُ تاريخُ
  //   المحلّ. ومن مُنح لوحةَ يومه لم يُمنح تاريخَه معها.
  reportViewMine: 'report.view.mine',
  reportViewBranch: 'report.view.branch',
  reportViewAll: 'report.view.all',
  reportExport: 'report.data.export',
} as const

export type Me = {
  userId: string | null
  sessionId: string | null
  username: string | null
  activeBranchId: string | null
  allowedBranchIds: string[]
  permissionsVersion: string | null
  permissions: string[]
}

export type AuthResponse = {
  accessToken: string
  accessExpiresAt: string
  refreshToken: string | null
  refreshExpiresAt: string
  sessionId: string
  mustChangePassword: boolean
  activeBranchId: string | null
}

export type Branch = {
  id: string
  code: string
  nameAr: string
  isActive: boolean
}

export type Warehouse = {
  id: string
  branchId: string
  code: string
  nameAr: string
  kind: string
  isActive: boolean
}
